'use client';

/**
 * DetailPanel Component
 *
 * @deprecated Use `SplitPanel` instead. DetailPanel is retained as an internal
 * overlay fallback used by SplitPanel for tablet/mobile breakpoints. Portal
 * code should not import DetailPanel directly — wrap content in SplitPanel's
 * `panel` prop and use `useSplitPanel()` for state management.
 *
 * A slide-out panel for displaying item details without losing list context.
 * Supports two variants: Summary (GitHub-style fixed width) and Detail (Notion-style resizable).
 * Includes responsive behavior: drawer on desktop, modal on tablet, full-screen on mobile.
 */

import type { Theme } from '@mui/material';
import { Box, Portal, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLayoutOffset, useReducedMotion } from '../../hooks';
import { glassEffects } from '../../theme/shadows';
import { durations } from '../../theme/transitions';
import { ResizeHandle } from '../SplitPanel/ResizeHandle';
import { SplitPanelContent } from '../SplitPanel/SplitPanelContent';
import { SplitPanelFooter } from '../SplitPanel/SplitPanelFooter';
import { SplitPanelHeader } from '../SplitPanel/SplitPanelHeader';

// Motion-enabled Box component for type-safe framer-motion integration
const MotionBox = motion.create(Box);

// ============================================================================
// Types
// ============================================================================

export type DetailPanelVariant = 'summary' | 'detail';

export interface DetailPanelProps {
  /** Whether the panel is open */
  open: boolean;
  /** Callback when panel should close */
  onClose: () => void;

  /** Panel variant: summary (fixed width) or detail (resizable) */
  variant?: DetailPanelVariant;
  /** Initial/fixed width in pixels */
  width?: number;
  /** Minimum width when resizing (detail variant) */
  minWidth?: number;
  /** Maximum width when resizing (detail variant) */
  maxWidth?: number;
  /** Panel anchor position */
  anchor?: 'left' | 'right';

  /** Panel title */
  title?: React.ReactNode;
  /** Subtitle or description */
  subtitle?: React.ReactNode;
  /** Actions to display in header (e.g., edit button) */
  headerActions?: React.ReactNode;
  /** Panel content */
  children: React.ReactNode;
  /** Footer content (e.g., action buttons) */
  footerContent?: React.ReactNode;

  /** Show full-screen on mobile (<768px) */
  fullScreenOnMobile?: boolean;
  /** Show as modal on tablet (768-1199px) */
  modalOnTablet?: boolean;

  /** Close on Escape key */
  closeOnEsc?: boolean;
  /** Trap focus within panel */
  trapFocus?: boolean;
  /** ARIA label for accessibility */
  ariaLabel?: string;

  /** Enable/disable animations */
  animate?: boolean;

  /** Storage key for persisting width (detail variant) */
  storageKey?: string;

  /**
   * Header offset to respect layout header (e.g., navbar height).
   * When provided, drawer mode will start below the header instead of viewport top.
   * Uses LayoutOffsetContext if available, otherwise falls back to this value.
   * @default 0
   */
  headerOffset?: number | string;

  /**
   * Whether to use the layout context offset (from LayoutOffsetProvider).
   * Set to false to ignore context and use headerOffset prop only.
   * @default true
   */
  useLayoutContext?: boolean;

  /**
   * Inset the drawer-mode panel by this many pixels from its anchor edge
   * (right or left). When the host renders a sibling rail/strip on the same
   * edge (e.g. ContextRail's always-visible 44px collapsed strip), this
   * keeps the strip visible to the right of the open panel instead of
   * letting the drawer cover it. Only applies to drawer mode (>= md) — the
   * modal-mode overlay is center-aligned so this prop is intentionally a
   * no-op there.
   * @default 0
   */
  edgeOffset?: number;
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_WIDTHS = {
  summary: 380,
  detail: 480,
} as const;

const MIN_WIDTH = 320;
const MAX_WIDTH = 600;

// ============================================================================
// Styled Components
// ============================================================================

interface DrawerPaperOptions {
  isDark: boolean;
  width: number;
  anchor: 'left' | 'right';
  hasOffset?: boolean;
  isMaterial?: boolean;
  paperBg?: string;
}

const getDrawerPaperStyles = ({
  isDark,
  width,
  anchor,
  hasOffset = false,
  isMaterial = false,
  paperBg,
}: DrawerPaperOptions) => {
  // Material Design 3: solid surface, no glass
  if (isMaterial) {
    const borderRadius = 16; // MD3 extra-large radius for side sheets
    return {
      width,
      height: '100%',
      display: 'flex',
      flexDirection: 'column' as const,
      background: paperBg || (isDark ? '#0D2137' : '#FFFFFF'),
      borderTopLeftRadius: hasOffset && anchor === 'right' ? borderRadius : 0,
      borderTopRightRadius: hasOffset && anchor === 'left' ? borderRadius : 0,
      borderLeft: anchor === 'right' ? `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}` : 'none',
      borderRight: anchor === 'left' ? `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}` : 'none',
      boxShadow:
        anchor === 'right'
          ? `-1px 0 3px rgba(0, 0, 0, ${isDark ? 0.3 : 0.12}), -4px 0 8px rgba(0, 0, 0, ${isDark ? 0.15 : 0.06})`
          : `1px 0 3px rgba(0, 0, 0, ${isDark ? 0.3 : 0.12}), 4px 0 8px rgba(0, 0, 0, ${isDark ? 0.15 : 0.06})`,
      overflow: 'hidden' as const,
    };
  }

  // Apple Liquid Glass
  const glass = isDark ? glassEffects.dark : glassEffects.light;
  const borderRadius = 20; // Liquid Glass — larger radius for panels

  return {
    width,
    height: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    background: isDark ? 'rgba(28, 28, 30, 0.72)' : 'rgba(255, 255, 255, 0.82)',
    backdropFilter: glass.backdropBlur,
    WebkitBackdropFilter: glass.backdropBlur,
    // Rounded corners on inner edge
    borderTopLeftRadius: hasOffset && anchor === 'right' ? borderRadius : 0,
    borderTopRightRadius: hasOffset && anchor === 'left' ? borderRadius : 0,
    borderLeft: anchor === 'right' ? `0.5px solid ${glass.border}` : 'none',
    borderRight: anchor === 'left' ? `0.5px solid ${glass.border}` : 'none',
    // Layered neutral shadow with specular highlight
    boxShadow: [
      anchor === 'right'
        ? `-4px 0 30px rgba(0, 0, 0, ${isDark ? 0.15 : 0.06})`
        : `4px 0 30px rgba(0, 0, 0, ${isDark ? 0.15 : 0.06})`,
      `inset 0 0.5px 0 ${isDark ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.5)'}`,
    ].join(', '),
    overflow: 'hidden' as const,
  };
};

const fullScreenStyles = (theme: Theme) => ({
  position: 'fixed' as const,
  inset: 0,
  zIndex: theme.zIndex.modal,
  display: 'flex',
  flexDirection: 'column' as const,
  background: theme.palette.background.default,
});

const getModalContainerStyles = (isDark: boolean, isMaterial = false, paperBg?: string) => {
  // Material Design 3: solid surface, no glass
  if (isMaterial) {
    return {
      width: '90%',
      maxWidth: 500,
      maxHeight: '90vh',
      display: 'flex',
      flexDirection: 'column' as const,
      background: paperBg || (isDark ? '#0D2137' : '#FFFFFF'),
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`,
      borderRadius: 28, // MD3 dialog radius
      boxShadow: `0 8px 24px rgba(0, 0, 0, ${isDark ? 0.3 : 0.15})`,
      overflow: 'hidden' as const,
    };
  }

  // Apple Liquid Glass
  const glass = isDark ? glassEffects.dark : glassEffects.light;
  return {
    width: '90%',
    maxWidth: 500,
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column' as const,
    background: isDark ? 'rgba(28, 28, 30, 0.72)' : 'rgba(255, 255, 255, 0.82)',
    backdropFilter: glass.backdropBlur,
    WebkitBackdropFilter: glass.backdropBlur,
    border: `0.5px solid ${glass.border}`,
    borderRadius: 20, // Liquid Glass panel radius
    boxShadow: `0 16px 48px rgba(0, 0, 0, ${isDark ? 0.28 : 0.12}), inset 0 0.5px 0 ${isDark ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.5)'}`,
    overflow: 'hidden' as const,
  };
};

const getBackdropStyles = (isDark: boolean) => ({
  position: 'fixed' as const,
  inset: 0,
  backgroundColor: isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.5)',
  backdropFilter: 'blur(2px)',
  WebkitBackdropFilter: 'blur(2px)',
  zIndex: 1299,
});

// ============================================================================
// Animation Variants
// ============================================================================

// Bézier easing curves (typed as tuples for framer-motion compatibility)
const easeOutCubic: [number, number, number, number] = [0.215, 0.61, 0.355, 1];
const easeInQuad: [number, number, number, number] = [0.4, 0, 1, 1];

const drawerVariants = {
  hidden: (anchor: 'left' | 'right') => ({
    x: anchor === 'right' ? '100%' : '-100%',
    opacity: 0.5,
  }),
  visible: {
    x: 0,
    opacity: 1,
    transition: {
      duration: durations.normal / 1000,
      ease: easeOutCubic,
    },
  },
  exit: (anchor: 'left' | 'right') => ({
    x: anchor === 'right' ? '100%' : '-100%',
    opacity: 0.5,
    transition: {
      duration: durations.fast / 1000,
      ease: easeInQuad,
    },
  }),
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.95, y: 20 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: durations.normal / 1000,
      ease: easeOutCubic,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 20,
    transition: {
      duration: durations.fast / 1000,
      ease: easeInQuad,
    },
  },
};

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: durations.fast / 1000 } },
  exit: { opacity: 0, transition: { duration: durations.fast / 1000 } },
};

// ============================================================================
// Component
// ============================================================================

export const DetailPanel: React.FC<DetailPanelProps> = ({
  open,
  onClose,
  variant = 'summary',
  width: initialWidth,
  minWidth = MIN_WIDTH,
  maxWidth = MAX_WIDTH,
  anchor = 'right',
  title,
  subtitle,
  headerActions,
  children,
  footerContent,
  fullScreenOnMobile = true,
  modalOnTablet = true,
  closeOnEsc = true,
  trapFocus = true,
  ariaLabel,
  animate = true,
  storageKey,
  headerOffset = 0,
  useLayoutContext = true,
  edgeOffset = 0,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMaterial = theme.designGuideline === 'material';
  const paperBg = theme.palette.background.paper;
  const reducedMotion = useReducedMotion();

  // Get layout offset from context if available
  const layoutOffset = useLayoutOffset();
  const effectiveOffset = useLayoutContext && layoutOffset.hasProvider ? layoutOffset.offset : headerOffset;
  const offsetValue = typeof effectiveOffset === 'number' ? `${effectiveOffset}px` : String(effectiveOffset);

  // Responsive breakpoints
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Width state for resizable variant
  const defaultWidth = initialWidth ?? DEFAULT_WIDTHS[variant];
  const [panelWidth, setPanelWidth] = useState<number>(() => {
    if (storageKey && typeof window !== 'undefined') {
      const stored = localStorage.getItem(`detail-panel-width-${storageKey}`);
      if (stored) {
        const parsed = parseInt(stored, 10);
        if (!Number.isNaN(parsed) && parsed >= minWidth && parsed <= maxWidth) {
          return parsed;
        }
      }
    }
    return defaultWidth;
  });

  // Refs
  const panelRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Determine render mode
  const renderMode: 'fullscreen' | 'modal' | 'drawer' = (() => {
    if (isMobile && fullScreenOnMobile) return 'fullscreen';
    if (isTablet && modalOnTablet) return 'modal';
    return 'drawer';
  })();

  // Handle resize
  const handleResize = useCallback(
    (newWidth: number) => {
      const clampedWidth = Math.min(Math.max(newWidth, minWidth), maxWidth);
      setPanelWidth(clampedWidth);
      if (storageKey && typeof window !== 'undefined') {
        localStorage.setItem(`detail-panel-width-${storageKey}`, String(clampedWidth));
      }
    },
    [minWidth, maxWidth, storageKey],
  );

  // Focus management
  useEffect(() => {
    if (open) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      // Focus panel after animation
      const timer = setTimeout(() => {
        if (panelRef.current) {
          const firstFocusable = panelRef.current.querySelector<HTMLElement>(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
          );
          firstFocusable?.focus();
        }
      }, 100);
      return () => clearTimeout(timer);
    } else {
      // Restore focus on close, only if the element is still in the document
      const previouslyFocused = previousFocusRef.current;
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
      return undefined;
    }
  }, [open]);

  // Keyboard handler
  useEffect(() => {
    if (!open || !closeOnEsc) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, closeOnEsc, onClose]);

  // Focus trap
  useEffect(() => {
    if (!open || !trapFocus || !panelRef.current) return;

    const panel = panelRef.current;
    const focusableElements = panel.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement.focus();
          e.preventDefault();
        }
      }
    };

    panel.addEventListener('keydown', handleTabKey);
    return () => panel.removeEventListener('keydown', handleTabKey);
  }, [open, trapFocus]);

  // Animation props
  const shouldAnimate = animate && !reducedMotion;

  // Only render auto-header if title, subtitle, or headerActions are provided
  const hasHeaderProps = title || subtitle || headerActions;

  // Panel content
  const panelContent = (
    <>
      {hasHeaderProps && (
        <SplitPanelHeader title={title} subtitle={subtitle} onClose={onClose} actions={headerActions} />
      )}
      <SplitPanelContent>{children}</SplitPanelContent>
      {footerContent && <SplitPanelFooter>{footerContent}</SplitPanelFooter>}
    </>
  );

  // Don't render if closed and not animating
  if (!open && !shouldAnimate) return null;

  // Full-screen mode (mobile)
  if (renderMode === 'fullscreen') {
    return (
      <Portal>
        <AnimatePresence>
          {open &&
            (shouldAnimate ? (
              <MotionBox
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-label={ariaLabel || 'Detail panel'}
                sx={fullScreenStyles(theme)}
                initial={{ opacity: 0, y: '100%' }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: '100%' }}
                transition={{ duration: durations.normal / 1000, ease: easeOutCubic }}
              >
                {panelContent}
              </MotionBox>
            ) : (
              <Box
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-label={ariaLabel || 'Detail panel'}
                sx={fullScreenStyles(theme)}
              >
                {panelContent}
              </Box>
            ))}
        </AnimatePresence>
      </Portal>
    );
  }

  // Modal mode (tablet)
  if (renderMode === 'modal') {
    return (
      <Portal>
        <AnimatePresence>
          {open && (
            <>
              {shouldAnimate ? (
                <MotionBox
                  onClick={onClose}
                  sx={getBackdropStyles(isDark)}
                  variants={backdropVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                />
              ) : (
                <Box onClick={onClose} sx={getBackdropStyles(isDark)} />
              )}
              <Box
                sx={{
                  position: 'fixed',
                  inset: 0,
                  zIndex: 1300,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                }}
              >
                {shouldAnimate ? (
                  <MotionBox
                    ref={panelRef}
                    role="dialog"
                    aria-modal="true"
                    aria-label={ariaLabel || 'Detail panel'}
                    sx={{ ...getModalContainerStyles(isDark, isMaterial, paperBg), pointerEvents: 'auto' }}
                    variants={modalVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  >
                    {panelContent}
                  </MotionBox>
                ) : (
                  <Box
                    ref={panelRef}
                    role="dialog"
                    aria-modal="true"
                    aria-label={ariaLabel || 'Detail panel'}
                    sx={{ ...getModalContainerStyles(isDark, isMaterial, paperBg), pointerEvents: 'auto' }}
                  >
                    {panelContent}
                  </Box>
                )}
              </Box>
            </>
          )}
        </AnimatePresence>
      </Portal>
    );
  }

  // Drawer mode (desktop)
  return (
    <Portal>
      <AnimatePresence custom={anchor}>
        {open && (
          <>
            {shouldAnimate ? (
              <MotionBox
                onClick={onClose}
                sx={getBackdropStyles(isDark)}
                variants={backdropVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              />
            ) : (
              <Box onClick={onClose} sx={getBackdropStyles(isDark)} />
            )}
            <Box
              sx={{
                position: 'fixed',
                top: offsetValue || 0,
                bottom: 0,
                // Drawer is inset from its anchor edge by `edgeOffset` (default 0)
                // so a sibling collapsed-rail strip can sit alongside it (see
                // ContextRail's COLLAPSED_RAIL_WIDTH forwarding).
                [anchor]: edgeOffset,
                zIndex: 1300,
                display: 'flex',
              }}
            >
              {variant === 'detail' && anchor === 'right' && (
                <ResizeHandle anchor="left" onResize={handleResize} currentWidth={panelWidth} />
              )}
              {shouldAnimate ? (
                <MotionBox
                  ref={panelRef}
                  role="dialog"
                  aria-modal="true"
                  aria-label={ariaLabel || 'Detail panel'}
                  sx={getDrawerPaperStyles({
                    isDark,
                    width: panelWidth,
                    anchor,
                    hasOffset: !!effectiveOffset,
                    isMaterial,
                    paperBg,
                  })}
                  custom={anchor}
                  variants={drawerVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  {panelContent}
                </MotionBox>
              ) : (
                <Box
                  ref={panelRef}
                  role="dialog"
                  aria-modal="true"
                  aria-label={ariaLabel || 'Detail panel'}
                  sx={getDrawerPaperStyles({
                    isDark,
                    width: panelWidth,
                    anchor,
                    hasOffset: !!effectiveOffset,
                    isMaterial,
                    paperBg,
                  })}
                >
                  {panelContent}
                </Box>
              )}
              {variant === 'detail' && anchor === 'left' && (
                <ResizeHandle anchor="right" onResize={handleResize} currentWidth={panelWidth} />
              )}
            </Box>
          </>
        )}
      </AnimatePresence>
    </Portal>
  );
};

export default DetailPanel;
